import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, Shield, Activity, Calendar, Settings, 
  Search, UserCheck, UserX, Trash2, ArrowLeft,
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/stats`)
      ]);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success('User role updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { is_active: !currentStatus });
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-white animate-pulse">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800" data-testid="admin-panel">
      {/* Header */}
      <div className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <Shield className="h-8 w-8 text-[#02b5ff]" />
                  Admin Panel
                </h1>
                <p className="text-gray-400 mt-1">Manage users and integrations</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#02b5ff]/20 rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-[#02b5ff]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.users.total}</div>
                  <div className="text-sm text-gray-400">Total Users</div>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#02b5ff]/20 rounded-xl flex items-center justify-center">
                  <Activity className="h-6 w-6 text-[#02b5ff]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.children}</div>
                  <div className="text-sm text-gray-400">Athletes</div>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#02b5ff]/20 rounded-xl flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-[#02b5ff]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.bookings}</div>
                  <div className="text-sm text-gray-400">Bookings</div>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#ff025f]/20 rounded-xl flex items-center justify-center">
                  <Settings className="h-6 w-6 text-[#ff025f]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats.measurements}</div>
                  <div className="text-sm text-gray-400">Measurements</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('users')}
            className={`${activeTab === 'users' ? 'bg-[#02b5ff] text-white' : 'bg-gray-800 text-gray-400'} rounded-full px-6`}
          >
            <Users className="h-4 w-4 mr-2" />
            Users
          </Button>
          <Button
            onClick={() => setActiveTab('integrations')}
            className={`${activeTab === 'integrations' ? 'bg-[#02b5ff] text-white' : 'bg-gray-800 text-gray-400'} rounded-full px-6`}
          >
            <Settings className="h-4 w-4 mr-2" />
            Integrations
          </Button>
        </div>

        {activeTab === 'users' && (
          <>
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-700 text-white rounded-xl"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-40 bg-gray-800 border-gray-700 text-white rounded-xl">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="athlete">Athlete</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Users Table */}
            <div className="bg-gray-800/50 rounded-2xl border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">User</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Role</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Joined</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-800/50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-white">{u.full_name}</div>
                          <div className="text-sm text-gray-400">{u.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Select 
                          value={u.role} 
                          onValueChange={(value) => updateUserRole(u.id, value)}
                          disabled={u.id === user?.id}
                        >
                          <SelectTrigger className="w-28 bg-gray-700 border-gray-600 text-white text-sm rounded-lg">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="coach">Coach</SelectItem>
                            <SelectItem value="parent">Parent</SelectItem>
                            <SelectItem value="athlete">Athlete</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          u.is_active !== false ? 'bg-[#02b5ff]/20 text-[#02b5ff]' : 'bg-[#ff025f]/20 text-[#ff025f]'
                        }`}>
                          {u.is_active !== false ? <UserCheck className="h-3 w-3" /> : <UserX className="h-3 w-3" />}
                          {u.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleUserStatus(u.id, u.is_active !== false)}
                            disabled={u.id === user?.id}
                            className="text-gray-400 hover:text-[#02b5ff]"
                          >
                            {u.is_active !== false ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteUser(u.id)}
                            disabled={u.id === user?.id}
                            className="text-gray-400 hover:text-[#ff025f]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && (
                <div className="text-center py-12 text-gray-400">No users found</div>
              )}
            </div>
          </>
        )}

        {activeTab === 'integrations' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* RCPCH Integration */}
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">RCPCH Growth Charts</h3>
                {stats.integrations.rcpch.configured ? (
                  <CheckCircle className="h-6 w-6 text-[#02b5ff]" />
                ) : (
                  <XCircle className="h-6 w-6 text-[#ff025f]" />
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">UK-WHO growth percentile calculations</p>
              <div className={`px-3 py-2 rounded-lg text-sm ${
                stats.integrations.rcpch.configured ? 'bg-[#02b5ff]/20 text-[#02b5ff]' : 'bg-[#ff025f]/20 text-[#ff025f]'
              }`}>
                {stats.integrations.rcpch.configured ? 'Active' : 'Not Configured'}
              </div>
            </div>

            {/* Everfit Integration */}
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Everfit</h3>
                {stats.integrations.everfit.configured ? (
                  <CheckCircle className="h-6 w-6 text-[#02b5ff]" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-[#02b5ff]" />
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">Workout plans, activity & metrics</p>
              <div className={`px-3 py-2 rounded-lg text-sm ${
                stats.integrations.everfit.configured ? 'bg-[#02b5ff]/20 text-[#02b5ff]' : 'bg-[#02b5ff]/20 text-[#02b5ff]'
              }`}>
                {stats.integrations.everfit.configured ? 'Active' : 'Mock Mode - Add API Key'}
              </div>
              {!stats.integrations.everfit.configured && (
                <p className="text-xs text-gray-500 mt-2">Add EVERFIT_API_KEY to backend/.env</p>
              )}
            </div>

            {/* Setmore Integration */}
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Setmore</h3>
                {stats.integrations.setmore.configured ? (
                  <CheckCircle className="h-6 w-6 text-[#02b5ff]" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-[#02b5ff]" />
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">Booking & appointment scheduling</p>
              <div className={`px-3 py-2 rounded-lg text-sm ${
                stats.integrations.setmore.configured ? 'bg-[#02b5ff]/20 text-[#02b5ff]' : 'bg-[#02b5ff]/20 text-[#02b5ff]'
              }`}>
                {stats.integrations.setmore.configured ? 'Active' : 'Local Mode - Add API Key'}
              </div>
              {!stats.integrations.setmore.configured && (
                <p className="text-xs text-gray-500 mt-2">Add SETMORE_API_KEY to backend/.env</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
